import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { apiGet } from "@/lib/api";
import type { StepMeData } from "@/lib/types";
import {
  isApp,
  stepPermission,
  stepSyncNow,
  stepToday,
  type StepCallbackWindow,
} from "@/lib/mdlApp";

interface UseStepMeResult {
  data: StepMeData | null;
  loading: boolean;
  error: string | null;
  /** 서버값과 센서값 중 «큰» 쪽 (숫자가 거꾸로 줄어들지 않게) */
  steps: number;
  /** 앱에서 걸음 권한 허용 여부 */
  permitted: boolean;
  reload: () => void;
}

const POLL_MS = 3000;

/**
 * 메인 「내 만보계」 위젯 훅.
 * - 마운트 시 step_me.php 로 오늘·주·월·최근7일 요약을 받는다.
 * - 앱(WebView) 안이면 3초마다 센서값을 다시 읽어 숫자를 끌어올린다.
 * - 카드가 화면에서 사라지거나 탭이 백그라운드로 가면 타이머를 멈춘다(배터리).
 */
export function useStepMe(
  enabled: boolean,
  cardRef?: RefObject<HTMLElement | null>
): UseStepMeResult {
  const [data, setData] = useState<StepMeData | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [liveSteps, setLiveSteps] = useState<number | null>(null);
  const [permitted, setPermitted] = useState<boolean>(() => stepPermission());
  const [inView, setInView] = useState(true);
  const [tick, setTick] = useState(0);
  // 처음 읽은 기기값 — 마운트 이후 «기기에서 늘어난 양»을 계산하는 기준점.
  // 서버 요약을 다시 받아 effect 가 재생성돼도 절대 초기화하지 않는다(언마운트까지 유지).
  const appBaseRef = useRef<number | null>(null);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  // 요약 조회
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    apiGet<StepMeData>("step_me.php")
      .then((res) => {
        if (active) setData(res.data);
      })
      .catch((e: Error) => {
        if (active) setError(e.message || "불러오지 못했습니다.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [enabled, tick]);

  // 화면에 보이는지 감지 — 안 보이면 폴링을 멈춘다
  useEffect(() => {
    if (!enabled || !cardRef) return;
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => setInView(entries[0]?.isIntersecting ?? true),
      { threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [enabled, cardRef]);

  // 실시간 갱신 (앱 전용)
  useEffect(() => {
    if (!enabled || !isApp()) return;

    const serverSteps = Number(data?.today?.steps ?? 0);
    let timer: number | null = null;

    const bump = (v: number) => {
      // 앱 재설치 등으로 기기 로컬 집계가 0부터 다시 시작하면 기기 절대값이
      // 서버값보다 작아진다. 그래서 절대값이 아니라 «마운트 이후 증가분»을 쓴다.
      if (appBaseRef.current === null) appBaseRef.current = v;
      const grown = Math.max(0, v - appBaseRef.current);
      // 서버 기준값 + 기기 증가분. 기기 절대값이 더 크면 그쪽을 쓴다.
      const candidate = Math.max(v, serverSteps + grown);
      setLiveSteps((prev) => (candidate > (prev ?? 0) ? candidate : prev));
    };

    const tickNow = () => {
      if (document.visibilityState === "hidden") return;
      bump(stepToday());
    };

    const start = () => {
      if (timer !== null) return;
      tickNow();
      timer = window.setInterval(tickNow, POLL_MS);
    };
    const stop = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    if (inView) start();

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        stop();
      } else if (inView) {
        start();
        stepSyncNow();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // 앱이 서버 업로드를 끝내면 호출된다 → 오늘 걸음 갱신 + 요약 재조회
    const onSynced = (steps?: number) => {
      if (typeof steps === "number") bump(steps);
      reload();
    };
    // 권한 요청 결과 → 허용되면 안내를 숨기고 재조회
    const onPermission = (granted?: boolean) => {
      setPermitted(!!granted);
      if (granted) reload();
    };

    const w = window as unknown as StepCallbackWindow;
    w.onMdlStepSynced = onSynced;
    w.onMdlStepPermission = onPermission;

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      const cw = window as unknown as StepCallbackWindow;
      cw.onMdlStepSynced = undefined;
      cw.onMdlStepPermission = undefined;
    };
  }, [enabled, data, inView, reload]);

  const serverSteps = Number(data?.today?.steps ?? 0);
  const steps = Math.max(serverSteps, liveSteps ?? 0);

  return { data, loading, error, steps, permitted, reload };
}